import {
  createContext,
  ReactNode,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

interface UpsellState {
  isVisible: boolean;
  hasBeenShown: boolean;
}

type BookmarksPanelTab = "bookmarks" | "quotes";

interface BookmarksPanelState {
  isOpen: boolean;
  activeTab: BookmarksPanelTab;
  selectedQuoteId: string | null;
}

interface ShareQuoteState {
  isOpen: boolean;
  quoteId: string | null;
  quoteText: string;
}

interface ModalsContextValue {
  upsell: UpsellState;
  showUpsell: () => void;
  hideUpsell: () => void;
  bookmarksPanel: BookmarksPanelState;
  setBookmarksPanel: Dispatch<SetStateAction<BookmarksPanelState>>;
  shareQuote: ShareQuoteState;
  openShareQuote: (quoteId: string, quoteText: string) => void;
  closeShareQuote: () => void;
}

export const ModalsContext = createContext<ModalsContextValue | null>(null);
ModalsContext.displayName = "ModalsContext";

interface ModalsProviderProps {
  children: ReactNode;
}

export function ModalsProvider({ children }: ModalsProviderProps) {
  const [isUpsellVisible, setIsUpsellVisible] = useState(false);
  const [hasUpsellBeenShown, setHasUpsellBeenShown] = useState(false);

  const [bookmarksPanel, setBookmarksPanel] = useState<BookmarksPanelState>({
    isOpen: false,
    activeTab: "bookmarks",
    selectedQuoteId: null,
  });

  const [shareQuote, setShareQuote] = useState<ShareQuoteState>({
    isOpen: false,
    quoteId: null,
    quoteText: "",
  });

  function openShareQuote(quoteId: string, quoteText: string) {
    setShareQuote({ isOpen: true, quoteId, quoteText });
  }

  function closeShareQuote() {
    setShareQuote({ isOpen: false, quoteId: null, quoteText: "" });
  }

  function showUpsell() {
    if (!hasUpsellBeenShown) {
      setIsUpsellVisible(true);
    }
  }

  function hideUpsell() {
    setIsUpsellVisible(false);
    setHasUpsellBeenShown(true);
  }

  return (
    <ModalsContext
      value={{
        upsell: {
          isVisible: isUpsellVisible,
          hasBeenShown: hasUpsellBeenShown,
        },
        showUpsell,
        hideUpsell,
        bookmarksPanel,
        setBookmarksPanel,
        shareQuote,
        openShareQuote,
        closeShareQuote,
      }}
    >
      {children}
    </ModalsContext>
  );
}
